import React, { useState, useEffect } from 'react'
import axios from 'axios'
import type { RAGDocument } from '@/types/rag'
import {
    RefreshCw,
    X,
    Copy,
    Check,
    Layers
} from 'lucide-react'
import { SecureDeleteModal } from '../../Shared/SecureDeleteModal'
import { useMediaQuery } from '../../../hooks/useMediaQuery'
import { UploadSection } from '../Sections/UploadSection'
import { DocumentsSection } from '../Sections/DocumentsSection'

interface AgentKnowledgeTabProps {
    session: any
    agentId: string
}

interface Chunk {
    id: string
    index: number
    content: string
    char_count: number
}

const API_BASE = (import.meta.env.VITE_RAG_API_URL || 'http://localhost:8000') + '/api/v1'

export function AgentKnowledgeTab({ session, agentId }: AgentKnowledgeTabProps) {
    const [documents, setDocuments] = useState<RAGDocument[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState<number | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
    const [viewChunksDoc, setViewChunksDoc] = useState<RAGDocument | null>(null)
    const [chunks, setChunks] = useState<Chunk[]>([])
    const [isLoadingChunks, setIsLoadingChunks] = useState(false)

    const [copiedChunkId, setCopiedChunkId] = useState<string | null>(null)
    const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set())
    const [isBulkDeleting, setIsBulkDeleting] = useState(false)
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

    // Secure Delete State
    const [deleteModalConfig, setDeleteModalConfig] = useState<{
        isOpen: boolean;
        title: string;
        description: React.ReactNode;
        verificationText: string;
        onConfirm: () => Promise<void>;
        isDeleting: boolean;
        error?: string | null;
    }>({
        isOpen: false,
        title: '',
        description: null,
        verificationText: '',
        onConfirm: async () => { },
        isDeleting: false
    })

    // Fetch documents on mount
    useEffect(() => {
        if (session?.access_token && agentId) {
            fetchDocuments()
        }
    }, [session, agentId])

    // Auto-polling for PROCESSING documents
    useEffect(() => {
        const hasProcessing = documents.some(d => d.status === 'PROCESSING')
        if (hasProcessing && session?.access_token) {
            const interval = setInterval(() => {
                fetchDocuments()
            }, 3000) // Poll every 3 seconds
            return () => clearInterval(interval)
        }
    }, [documents, session])

    const fetchDocuments = async () => {
        if (!session?.access_token || !agentId) return

        setIsLoading(true)
        try {
            const config = {
                headers: { Authorization: `Bearer ${session.access_token}` },
                params: {
                    limit: 100,
                    agent_id: agentId // AGENT-SCOPED: Filter by agent
                }
            }
            const response = await axios.get(`${API_BASE}/knowledge/documents`, config)

            const docs = Array.isArray(response.data) ? response.data : (response.data?.documents || [])
            setDocuments(docs)
        } catch (err: any) {
            console.error('Failed to fetch agent documents:', err)
            setError(err.response?.data?.detail || 'Failed to load documents')
            setDocuments([])
        } finally {
            setIsLoading(false)
        }
    }

    const handleFileUpload = async (file: File) => {
        if (!session?.access_token || !agentId) return

        // Validate file
        const validTypes = ['application/pdf', 'text/plain', 'text/markdown']
        const maxSize = 5 * 1024 * 1024 // 5MB

        const isTypeValid = validTypes.includes(file.type) || file.name.match(/\.(pdf|txt|md)$/i)

        if (!isTypeValid) {
            setError('Invalid file type. Only PDF, TXT, and MD files are supported.')
            return
        }

        if (file.size > maxSize) {
            setError('File too large. Maximum size is 5MB.')
            return
        }

        setError(null)
        setUploadProgress(0)

        try {
            const formData = new FormData()
            formData.append('file', file)
            formData.append('agent_id', agentId) // AGENT-SCOPED: Include agent_id

            const config = {
                headers: {
                    Authorization: `Bearer ${session.access_token}`,
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent: any) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                    setUploadProgress(percentCompleted)
                }
            }

            await axios.post(`${API_BASE}/knowledge/upload`, formData, config)

            setUploadProgress(null)
            fetchDocuments() // Refresh list
        } catch (err: any) {
            console.error('Upload failed:', err)
            if (err.response?.status === 409) {
                setError(`File '${file.name}' already exists. Please rename it before uploading.`)
            } else if (err.response?.status === 403) {
                setError('You do not have permission to upload to this agent.')
            } else {
                setError(err.response?.data?.detail || 'Upload failed')
            }

            setUploadProgress(null)
        }
    }

    const handleDelete = (documentId: string, filename: string) => {
        if (!session?.access_token) return

        setDeleteModalConfig({
            isOpen: true,
            title: `Delete Document?`,
            description: (
                <div>
                    <p>Are you sure you want to permanently delete <strong>{filename}</strong>?</p>
                    <p className="mt-2 text-gray-400">This action cannot be undone and will remove the document and all its indexed chunks from the agent's knowledge base.</p>
                </div>
            ),
            verificationText: filename,
            isDeleting: false,
            onConfirm: async () => {
                setDeleteModalConfig(prev => ({ ...prev, isDeleting: true, error: null }))
                try {
                    const config = { headers: { Authorization: `Bearer ${session.access_token}` } }
                    await axios.delete(`${API_BASE}/knowledge/documents/${documentId}`, config)

                    setDocuments(docs => docs.filter(d => d.id !== documentId))
                    setExpandedRows(prev => {
                        const next = new Set(prev)
                        next.delete(documentId)
                        return next
                    })
                } catch (err: any) {
                    console.error('Failed to delete document:', err)
                    setDeleteModalConfig(prev => ({ ...prev, isDeleting: false, error: 'Failed to delete document. Please try again.' }))
                    throw err
                }
            }
        })
    }

    const handleViewChunks = async (doc: RAGDocument) => {
        if (!session?.access_token) return

        setViewChunksDoc(doc)
        setIsLoadingChunks(true)
        setChunks([])

        try {
            const config = {
                headers: { Authorization: `Bearer ${session.access_token}` },
                params: { limit: 100 }
            }
            const response = await axios.get(`${API_BASE}/knowledge/documents/${doc.id}/chunks`, config)
            setChunks(response.data?.chunks || [])
        } catch (err: any) {
            console.error('Failed to fetch chunks:', err)
            setError('Failed to load chunks.')
        } finally {
            setIsLoadingChunks(false)
        }
    }

    const copyToClipboard = async (text: string, chunkId: string) => {
        try {
            await navigator.clipboard.writeText(text)
            setCopiedChunkId(chunkId)
            setTimeout(() => setCopiedChunkId(null), 2000)
        } catch (err) {
            console.error('Failed to copy:', err)
        }
    }

    const handleBulkDelete = () => {
        if (!session?.access_token || selectedDocs.size === 0) return

        const count = selectedDocs.size
        const verificationText = `DELETE ${count}`

        setDeleteModalConfig({
            isOpen: true,
            title: `Delete ${count} Documents?`,
            description: (
                <div>
                    <p>Are you sure you want to delete <strong>{count} selected documents</strong>?</p>
                    <p className="mt-2 text-gray-400">This action is irreversible. All selected documents and their derived data will be permanently wiped from this agent's knowledge base.</p>
                </div>
            ),
            verificationText: verificationText,
            isDeleting: false,
            onConfirm: async () => {
                setDeleteModalConfig(prev => ({ ...prev, isDeleting: true, error: null }))
                setIsBulkDeleting(true)
                try {
                    const config = { headers: { Authorization: `Bearer ${session.access_token}` } }

                    const deletePromises = Array.from(selectedDocs).map(id =>
                        axios.delete(`${API_BASE}/knowledge/documents/${id}`, config)
                    )

                    await Promise.allSettled(deletePromises)

                    await fetchDocuments()
                    setSelectedDocs(new Set())

                } catch (err: any) {
                    console.error('Bulk delete failed:', err)
                    setDeleteModalConfig(prev => ({ ...prev, isDeleting: false, error: 'Some documents failed to delete.' }))
                    throw err
                } finally {
                    setIsBulkDeleting(false)
                }
            }
        })
    }

    const toggleSelectAll = () => {
        if (selectedDocs.size === documents.length) {
            setSelectedDocs(new Set())
        } else {
            setSelectedDocs(new Set(documents.map((d: RAGDocument) => d.id)))
        }
    }

    const toggleRow = (id: string) => {
        const newExpanded = new Set(expandedRows)
        if (newExpanded.has(id)) {
            newExpanded.delete(id)
        } else {
            newExpanded.add(id)
        }
        setExpandedRows(newExpanded)
    }

    const isMobile = useMediaQuery('(max-width: 640px)')

    return (
        <div className="h-full flex flex-col overflow-hidden">
            <div className="flex-1 flex overflow-hidden">
                {/* SECTION 1: Upload (30%) */}
                <div className="w-[30%] border-r border-white/10">
                    <UploadSection
                        uploadProgress={uploadProgress}
                        isDragging={isDragging}
                        error={error}
                        isMobile={isMobile}
                        onFileUpload={handleFileUpload}
                        onDragStateChange={setIsDragging}
                    />
                </div>

                {/* SECTION 2: Documents (70%) */}
                <div className="w-[70%]">
                    <DocumentsSection
                        documents={documents}
                        selectedDocs={selectedDocs}
                        viewMode={viewMode}
                        isLoading={isLoading}
                        isBulkDeleting={isBulkDeleting}
                        isMobile={isMobile}
                        expandedRows={expandedRows}
                        onViewModeChange={setViewMode}
                        onToggleSelect={(id, selected) => {
                            const newSelected = new Set(selectedDocs)
                            selected ? newSelected.add(id) : newSelected.delete(id)
                            setSelectedDocs(newSelected)
                        }}
                        onView={handleViewChunks}
                        onDelete={(id) => {
                            const doc = documents.find(d => d.id === id)
                            if (doc) handleDelete(id, doc.filename)
                        }}
                        onBulkDelete={handleBulkDelete}
                        onToggleRow={toggleRow}
                        onToggleSelectAll={toggleSelectAll}
                        totalCount={documents.length}
                    />
                </div>
            </div>

            {/* Chunks Modal */}
            {
                viewChunksDoc && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setViewChunksDoc(null)}>
                        <div className="bg-[#1a1a1a] border border-white/10 rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                            <div className="p-4 border-b border-white/10 flex items-center justify-between bg-white/5">
                                <div className="flex items-center gap-3">
                                    <Layers className="w-5 h-5 text-indigo-400" />
                                    <div>
                                        <h3 className="text-white font-medium text-sm">Document Chunks</h3>
                                        <p className="text-gray-400 text-xs">{viewChunksDoc.filename}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setViewChunksDoc(null)}
                                    className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                {isLoadingChunks ? (
                                    <div className="flex flex-col items-center justify-center py-12">
                                        <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin mb-3" />
                                        <p className="text-gray-400 text-xs">Loading chunks...</p>
                                    </div>
                                ) : chunks.length === 0 ? (
                                    <div className="text-center py-12 text-gray-500">
                                        No chunks found for this document.
                                    </div>
                                ) : (
                                    chunks.map((chunk) => (
                                        <div key={chunk.id} className="bg-black/40 border border-white/5 rounded-lg p-4 group hover:border-indigo-500/30 transition-colors">
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded text-[10px] font-mono">
                                                        CHUNK #{chunk.index}
                                                    </span>
                                                    <span className="text-gray-500 text-[10px] font-mono">
                                                        {chunk.char_count} chars
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={() => copyToClipboard(chunk.content, chunk.id)}
                                                    className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded text-gray-400 hover:text-white transition-all"
                                                    title="Copy content"
                                                >
                                                    {copiedChunkId === chunk.id ? (
                                                        <Check className="w-3.5 h-3.5 text-green-400" />
                                                    ) : (
                                                        <Copy className="w-3.5 h-3.5" />
                                                    )}
                                                </button>
                                            </div>
                                            <div className="text-gray-300 text-xs font-mono whitespace-pre-wrap leading-relaxed">
                                                {chunk.content}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            <div className="p-3 border-t border-white/10 bg-white/5 flex justify-between items-center text-xs text-gray-500">
                                <span>Total Chunks: {chunks.length}</span>
                                <span className="font-mono text-[10px] opacity-50">ID: {viewChunksDoc.id}</span>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Secure Delete Modal */}
            <SecureDeleteModal
                isOpen={deleteModalConfig.isOpen}
                onClose={() => setDeleteModalConfig(prev => ({ ...prev, isOpen: false }))}
                onConfirm={deleteModalConfig.onConfirm}
                title={deleteModalConfig.title}
                description={deleteModalConfig.description}
                verificationText={deleteModalConfig.verificationText}
                isDeleting={deleteModalConfig.isDeleting}
                error={deleteModalConfig.error}
            />
        </div>
    )
}
